import React, { useState, useEffect, useCallback, useRef } from 'react';
import { faultsService, techniciansService, stationsService, sitesService } from '../services/api';
import { useTheme } from '../layouts/DashboardLayout';
import { useAuth, ROLES } from '../contexts/AuthContext.jsx';
import { Html5QrcodeScanner } from 'html5-qrcode';
import {
  AlertTriangle,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  Wrench,
  User,
  Phone,
  ChevronDown,
  ChevronUp,
  X,
  Eye,
  Edit,
  Trash2,
  BarChart3,
  List,
  Users,
  AlertOctagon,
  TrendingUp,
  MapPin,
  Zap,
  Calendar,
  FileText,
  Download,
  History,
  Database,
  Camera,
  Image,
  Upload,
  Navigation,
  QrCode,
  Scan,
  Building2,
  Layers,
  Settings,
  Package
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';
import TechnicianLocationMap from '../components/TechnicianLocationMap';

// ==================== CONSTANTS ====================

// İş Tipleri
const JOB_TYPES = {
  fault: { 
    id: 'fault', 
    label: 'Arıza Bildirimi', 
    icon: AlertTriangle, 
    emoji: '🔧', 
    color: 'bg-red-500',
    description: 'Şarj istasyonu arıza kaydı',
    requiredSpecialties: ['general', 'ac_charging', 'dc_fast', 'electrical', 'software']
  },
  installation: { 
    id: 'installation', 
    label: 'Montaj/Kurulum', 
    icon: Package, 
    emoji: '🔩', 
    color: 'bg-blue-500',
    description: 'Yeni cihaz montajı veya kurulum',
    requiredSpecialties: ['installation', 'electrical']
  },
  maintenance: { 
    id: 'maintenance', 
    label: 'Periyodik Bakım', 
    icon: Settings, 
    emoji: '🔍', 
    color: 'bg-green-500',
    description: 'Düzenli bakım ve kontrol',
    requiredSpecialties: ['general']
  }
};

const PRIORITY_CONFIG = {
  critical: { label: 'Kritik', color: 'bg-red-500', textColor: 'text-red-500', bgLight: 'bg-red-50 dark:bg-red-900/20' },
  high: { label: 'Yüksek', color: 'bg-orange-500', textColor: 'text-orange-500', bgLight: 'bg-orange-50 dark:bg-orange-900/20' },
  medium: { label: 'Orta', color: 'bg-yellow-500', textColor: 'text-yellow-500', bgLight: 'bg-yellow-50 dark:bg-yellow-900/20' },
  low: { label: 'Düşük', color: 'bg-blue-500', textColor: 'text-blue-500', bgLight: 'bg-blue-50 dark:bg-blue-900/20' }
};

const STATUS_CONFIG = {
  new: { label: 'Yeni', color: 'bg-blue-500', icon: AlertTriangle },
  investigating: { label: 'İnceleniyor', color: 'bg-purple-500', icon: Search },
  assigned: { label: 'Teknisyen Atandı', color: 'bg-indigo-500', icon: User },
  in_repair: { label: 'Onarımda', color: 'bg-amber-500', icon: Wrench },
  resolved: { label: 'Çözüldü', color: 'bg-green-500', icon: CheckCircle },
  closed: { label: 'Kapatıldı', color: 'bg-slate-500', icon: XCircle }
};

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

// ==================== JOB TYPE SELECT MODAL ====================

const JobTypeSelectModal = ({ isOpen, onClose, onSelect }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card rounded-2xl shadow-xl w-full max-w-lg p-6 border border-border">
        <h2 className="text-xl font-bold text-foreground mb-2">Yeni İş Talebi</h2>
        <p className="text-sm text-muted-foreground mb-6">Ne tür bir iş kaydı oluşturmak istiyorsunuz?</p>
        
        <div className="space-y-3">
          {Object.values(JOB_TYPES).map(jobType => {
            const Icon = jobType.icon;
            return (
              <button
                key={jobType.id}
                onClick={() => onSelect(jobType.id)}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-border hover:border-primary hover:bg-primary/5 transition-all group"
                data-testid={`job-type-${jobType.id}`}
              >
                <div className={`w-12 h-12 rounded-xl ${jobType.color} flex items-center justify-center text-white`}>
                  <span className="text-2xl">{jobType.emoji}</span>
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                    {jobType.label}
                  </h3>
                  <p className="text-sm text-muted-foreground">{jobType.description}</p>
                </div>
                <ChevronDown className="w-5 h-5 text-muted-foreground -rotate-90 group-hover:text-primary transition-colors" />
              </button>
            );
          })}
        </div>
        
        <button
          onClick={onClose}
          className="mt-6 w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          İptal
        </button>
      </div>
    </div>
  );
};

// ==================== UTILITY COMPONENTS ====================

const Badge = ({ children, variant = 'default', className = '' }) => {
  const variants = {
    default: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
    success: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    warning: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    danger: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${config.bgLight} ${config.textColor}`}>
      <span className={`w-2 h-2 rounded-full ${config.color}`} />
      {config.label}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.new;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

const Card = ({ children, className = '', ...props }) => (
  <div className={`bg-card border border-border rounded-xl p-4 ${className}`} {...props}>
    {children}
  </div>
);

const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    red: 'from-red-500 to-red-600',
    orange: 'from-orange-500 to-orange-600',
    purple: 'from-purple-500 to-purple-600'
  };
  
  return (
    <Card className="relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-3xl font-bold">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]} shadow-lg`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      {trend && (
        <div className="mt-3 flex items-center gap-1 text-sm">
          <TrendingUp className={`w-4 h-4 ${trend > 0 ? 'text-green-500' : 'text-red-500'}`} />
          <span className={trend > 0 ? 'text-green-500' : 'text-red-500'}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
          <span className="text-muted-foreground">son 7 gün</span>
        </div>
      )}
    </Card>
  );
};

// ==================== MODAL COMPONENT ====================

const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl'
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={`w-full ${sizeClasses[size]} bg-card border border-border rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col`}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-accent transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {children}
        </div>
      </div>
    </div>
  );
};

// ==================== TABS COMPONENT ====================

const TabButton = ({ active, onClick, icon: Icon, label, badge }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
      active
        ? 'bg-primary text-primary-foreground shadow-lg'
        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
    }`}
  >
    <Icon className="w-4 h-4" />
    <span>{label}</span>
    {badge !== undefined && (
      <span className={`px-2 py-0.5 text-xs rounded-full ${
        active ? 'bg-white/20' : 'bg-accent'
      }`}>
        {badge}
      </span>
    )}
  </button>
);

// ==================== USTA ATAMA MODAL ====================

// Haversine mesafe hesaplama (km)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (!lat1 || !lon1 || !lat2 || !lon2) return null;
  
  const R = 6371; // Dünya yarıçapı (km)
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return Math.round(R * c);
};

// Türkiye illeri koordinatları (yaklaşık merkez)
const CITY_COORDINATES = {
  'İstanbul': { lat: 41.0082, lon: 28.9784 },
  'Ankara': { lat: 39.9334, lon: 32.8597 },
  'İzmir': { lat: 38.4192, lon: 27.1287 },
  'Bursa': { lat: 40.1885, lon: 29.0610 },
  'Antalya': { lat: 36.8969, lon: 30.7133 },
  'Adana': { lat: 37.0000, lon: 35.3213 },
  'Konya': { lat: 37.8746, lon: 32.4932 },
  'Gaziantep': { lat: 37.0662, lon: 37.3833 },
  'Kocaeli': { lat: 40.8533, lon: 29.8815 },
  'Mersin': { lat: 36.8121, lon: 34.6415 },
  'Diyarbakır': { lat: 37.9144, lon: 40.2306 },
  'Kayseri': { lat: 38.7312, lon: 35.4787 },
  'Eskişehir': { lat: 39.7767, lon: 30.5206 },
  'Samsun': { lat: 41.2928, lon: 36.3313 },
  'Denizli': { lat: 37.7765, lon: 29.0864 },
  'Şanlıurfa': { lat: 37.1591, lon: 38.7969 },
  'Malatya': { lat: 38.3552, lon: 38.3095 },
  'Trabzon': { lat: 41.0027, lon: 39.7168 },
  'Erzurum': { lat: 39.9043, lon: 41.2679 },
  'Van': { lat: 38.4891, lon: 43.4089 },
  'Ağrı': { lat: 39.7191, lon: 43.0503 },
  'Manisa': { lat: 38.6191, lon: 27.4289 },
  'Balıkesir': { lat: 39.6484, lon: 27.8826 },
  'Sakarya': { lat: 40.6940, lon: 30.4358 },
  'Tekirdağ': { lat: 40.9833, lon: 27.5167 },
  'Muğla': { lat: 37.2153, lon: 28.3636 },
  'Aydın': { lat: 37.8560, lon: 27.8416 },
  'Hatay': { lat: 36.4018, lon: 36.3498 },
  'Kahramanmaraş': { lat: 37.5858, lon: 36.9371 },
  'Mardin': { lat: 37.3212, lon: 40.7245 }
};

// Komşu il tanımları
const NEIGHBOR_CITIES = {
  'İstanbul': ['Kocaeli', 'Tekirdağ', 'Bursa', 'Sakarya', 'Yalova', 'Edirne', 'Kırklareli'],
  'Ankara': ['Eskişehir', 'Konya', 'Kırıkkale', 'Çankırı', 'Bolu', 'Kırşehir', 'Aksaray'],
  'İzmir': ['Manisa', 'Aydın', 'Balıkesir', 'Denizli', 'Uşak', 'Muğla'],
  'Bursa': ['İstanbul', 'Kocaeli', 'Sakarya', 'Bilecik', 'Balıkesir', 'Yalova', 'Kütahya'],
  'Antalya': ['Muğla', 'Burdur', 'Isparta', 'Konya', 'Mersin', 'Karaman'],
  'Kocaeli': ['İstanbul', 'Sakarya', 'Bursa', 'Yalova', 'Bolu'],
  'Konya': ['Ankara', 'Aksaray', 'Karaman', 'Mersin', 'Antalya', 'Isparta', 'Afyon', 'Eskişehir'],
  'Adana': ['Mersin', 'Hatay', 'Osmaniye', 'Kahramanmaraş', 'Gaziantep', 'Niğde', 'Kayseri'],
  'Gaziantep': ['Adana', 'Kahramanmaraş', 'Adıyaman', 'Şanlıurfa', 'Kilis', 'Hatay'],
  'Tekirdağ': ['İstanbul', 'Edirne', 'Kırklareli', 'Çanakkale'],
  'Sakarya': ['İstanbul', 'Kocaeli', 'Bursa', 'Bilecik', 'Bolu', 'Düzce'],
};

const AssignTechnicianModal = ({ isOpen, onClose, fault, technicians: propTechnicians = [], sites = [], onAssign }) => {
  const [loading, setLoading] = useState(false);
  const [selectedTech, setSelectedTech] = useState(null);
  const [isWarranty, setIsWarranty] = useState(false);
  const [warrantyNotes, setWarrantyNotes] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterAvailable, setFilterAvailable] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState(60);
  const [technicians, setTechnicians] = useState([]);
  const [techLoading, setTechLoading] = useState(false);
  const [contractedTechs, setContractedTechs] = useState([]);

  // İş tipine göre önerilen uzmanlık alanları
  const getRequiredSpecialties = () => {
    const jobType = fault?.job_type || 'fault';
    const specialtyMap = {
      fault: ['general', 'ac_charging', 'dc_fast', 'electrical', 'software'],
      installation: ['installation', 'electrical'],
      maintenance: ['general']
    };
    return specialtyMap[jobType] || specialtyMap.fault;
  };

  // Modal açıldığında teknisyenleri ve sözleşmeleri yükle
  useEffect(() => {
    if (isOpen) {
      const fetchData = async () => {
        setTechLoading(true);
        try {
          const token = localStorage.getItem('hims_token');
          // API URL - window.location.origin kullan, env değişkeni yoksa
          const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
          
          console.log('AssignModal - Fetching technicians from:', `${API_URL}/api/technicians`);
          
          // Teknisyenleri yükle
          const techRes = await fetch(`${API_URL}/api/technicians`, {
            method: 'GET',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          console.log('AssignModal - Response status:', techRes.status);
          
          if (techRes.ok) {
            const data = await techRes.json();
            console.log('AssignModal - Technicians loaded:', data.length, data);
            setTechnicians(Array.isArray(data) ? data : []);
          } else {
            const errorText = await techRes.text();
            console.error('AssignModal - API error:', techRes.status, errorText);
            setTechnicians([]);
          }
          
          // Sözleşmeli ustaları yükle (site varsa)
          if (fault?.site_id) {
            try {
              const contractRes = await fetch(`${API_URL}/api/sites/${fault.site_id}/contracted-technicians`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (contractRes.ok) {
                const contractData = await contractRes.json();
                setContractedTechs(contractData.map(c => c.technician_id));
              }
            } catch (contractError) {
              console.error('AssignModal - Contract fetch error:', contractError);
            }
          }
        } catch (error) {
          console.error('AssignModal - Failed to load data:', error);
          setTechnicians([]);
        } finally {
          setTechLoading(false);
        }
      };
      fetchData();
    }
  }, [isOpen, fault?.site_id]);

  // Site bilgisini bul
  const faultSite = fault?.site_id ? sites.find(s => s.id === fault.site_id) : null;
  const siteCity = faultSite?.city || faultSite?.address?.split(',').pop()?.trim() || '';
  const siteCoords = CITY_COORDINATES[siteCity] || null;

  // Teknisyenleri filtrele
  const filteredTechnicians = technicians.filter(tech => {
    // Müsaitlik filtresi
    if (filterAvailable && !tech.is_available) return false;
    
    // Tip filtresi
    if (filterType && tech.technician_type !== filterType) return false;
    
    // Arama filtresi
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!tech.full_name?.toLowerCase().includes(search) && 
          !tech.phone?.includes(search) &&
          !tech.company_name?.toLowerCase().includes(search)) {
        return false;
      }
    }
    
    return true;
  });

  // Mesafe hesapla ve ekle
  const techsWithDistance = filteredTechnicians.map(tech => {
    const techCoords = tech.latitude && tech.longitude 
      ? { lat: tech.latitude, lon: tech.longitude }
      : CITY_COORDINATES[tech.city] || null;
    
    const distance = siteCoords && techCoords 
      ? calculateDistance(siteCoords.lat, siteCoords.lon, techCoords.lat, techCoords.lon)
      : null;
    
    // Bölge eşleşmesi kontrolü
    const inRegion = tech.service_regions?.some(r => 
      siteCity.toLowerCase().includes(r.toLowerCase()) || r.toLowerCase().includes(siteCity.toLowerCase())
    );
    
    // Sözleşmeli usta kontrolü
    const isContracted = contractedTechs.includes(tech.id);
    
    // Uzmanlık eşleşmesi kontrolü
    const requiredSpecialties = getRequiredSpecialties();
    const hasMatchingSpecialty = tech.specialties?.some(s => requiredSpecialties.includes(s));
    
    // Aynı il kontrolü
    const techCity = tech.city?.trim() || '';
    const isSameCity = techCity.toLowerCase() === siteCity.toLowerCase();
    
    // Komşu il kontrolü
    const neighborCities = NEIGHBOR_CITIES[siteCity] || [];
    const isNeighborCity = neighborCities.some(nc => nc.toLowerCase() === techCity.toLowerCase());
    
    return { ...tech, distance, inRegion, isContracted, hasMatchingSpecialty, isSameCity, isNeighborCity };
  });

  // Teknisyenleri sırala - sözleşmeli > aynı il+branş > komşu il+branş > uzak il+branş > aynı il farklı branş > diğer
  const sortedTechnicians = [...techsWithDistance].sort((a, b) => {
    // 1. Sözleşmeli ustalar her zaman en üstte
    if (a.isContracted && !b.isContracted) return -1;
    if (!a.isContracted && b.isContracted) return 1;
    
    // 2. Aynı il + doğru branş
    const aScore2 = a.isSameCity && a.hasMatchingSpecialty ? 1 : 0;
    const bScore2 = b.isSameCity && b.hasMatchingSpecialty ? 1 : 0;
    if (aScore2 !== bScore2) return bScore2 - aScore2;
    
    // 3. Komşu il + doğru branş
    const aScore3 = a.isNeighborCity && a.hasMatchingSpecialty ? 1 : 0;
    const bScore3 = b.isNeighborCity && b.hasMatchingSpecialty ? 1 : 0;
    if (aScore3 !== bScore3) return bScore3 - aScore3;
    
    // 4. Uzak il + doğru branş (mesafeye göre)
    if (a.hasMatchingSpecialty && b.hasMatchingSpecialty) {
      if (a.distance !== null && b.distance !== null) {
        if (a.distance !== b.distance) return a.distance - b.distance;
      }
    }
    if (a.hasMatchingSpecialty && !b.hasMatchingSpecialty) return -1;
    if (!a.hasMatchingSpecialty && b.hasMatchingSpecialty) return 1;
    
    // 5. Aynı il + farklı branş
    if (a.isSameCity && !b.isSameCity) return -1;
    if (!a.isSameCity && b.isSameCity) return 1;
    
    // 6. Müsaitlik
    const aAvailable = a.availability_status === 'available' || a.is_available;
    const bAvailable = b.availability_status === 'available' || b.is_available;
    if (aAvailable && !bAvailable) return -1;
    if (!aAvailable && bAvailable) return 1;
    
    // 7. Mesafe
    if (a.distance !== null && b.distance !== null) {
      return a.distance - b.distance;
    }
    
    // 8. Puan
    return (b.average_rating || 0) - (a.average_rating || 0);
  }).slice(0, 10); // Max 10 usta göster

  // Maliyet hesapla (mesafeye göre yol masrafı)
  const calculateCost = (tech) => {
    const laborCost = isWarranty ? (tech.warranty_rate || 0) : (tech.per_job_rate || 0);
    let travelCost = tech.travel_allowance || 0;
    
    // Uzak mesafe için ekstra yol masrafı (100km üzeri için km başına 2₺)
    if (tech.distance && tech.distance > 100) {
      travelCost += (tech.distance - 100) * 2;
    }
    
    return { laborCost, travelCost, total: laborCost + travelCost, distance: tech.distance };
  };

  // İş emri oluştur
  const handleAssign = async () => {
    if (!selectedTech || !fault) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('hims_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      // 1. Önce arızanın technician_id alanını güncelle
      const faultUpdateRes = await fetch(`${API_URL}/api/faults/${fault.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          technician_id: selectedTech.id,
          status: fault.status === 'open' || fault.status === 'new' ? 'assigned' : fault.status
        })
      });

      if (!faultUpdateRes.ok) {
        const err = await faultUpdateRes.json();
        console.error('Arıza güncelleme hatası:', err);
        alert('Usta ataması kaydedilemedi: ' + (err.detail || 'Bilinmeyen hata'));
        setLoading(false);
        return;
      }

      // 2. Sonra iş emri oluştur
      const workOrderData = {
        fault_id: fault.id,
        technician_id: selectedTech.id,
        site_id: fault.site_id,
        station_id: fault.station_id,
        location_id: fault.location_id || null,
        title: fault.title,
        description: fault.description || `Arıza: ${fault.title}`,
        priority: fault.priority,
        is_warranty: isWarranty,
        warranty_notes: warrantyNotes || null,
        scheduled_date: scheduledDate ? new Date(scheduledDate).toISOString() : null,
        estimated_duration: estimatedDuration,
        assigned_by: ''
      };

      const res = await fetch(`${API_URL}/api/work-orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(workOrderData)
      });

      if (res.ok) {
        const result = await res.json();
        onAssign(result);
        onClose();
      } else {
        const err = await res.json();
        // İş emri oluşturulamasa bile usta ataması yapıldı
        console.warn('İş emri oluşturulamadı ama usta atandı:', err);
        onAssign({ technician_id: selectedTech.id });
        onClose();
      }
    } catch (error) {
      console.error('İş emri oluşturma hatası:', error);
      alert('İş emri oluşturulurken bir sorun oluştu. Lütfen tekrar deneyin');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !fault) return null;

  const TECH_TYPES = {
    hims_team: { label: 'HIMS Ekibi', color: 'blue' },
    subcontractor: { label: 'Taşeron', color: 'orange' },
    service_company: { label: 'Servis Firması', color: 'purple' }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Usta / Teknisyen Ata" size="xl">
      <div className="space-y-4">
        {/* Arıza Bilgisi - Ustanın Göreceği Detaylar */}
        <div className="p-4 rounded-xl bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-red-500/20">
              <AlertTriangle className="w-5 h-5 text-red-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">{fault.title}</h3>
              <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {fault.site_name || 'Bilinmiyor'}
                </span>
                <span className="flex items-center gap-1">
                  <Zap className="w-4 h-4" />
                  {fault.station_name || fault.station_id}
                </span>
              </div>
              {/* Sorun Açıklaması */}
              {fault.description && (
                <div className="mt-2 p-2 rounded-lg bg-background/50 border border-border">
                  <p className="text-sm font-medium text-muted-foreground mb-1">Sorun Açıklaması:</p>
                  <p className="text-sm">{fault.description}</p>
                </div>
              )}
            </div>
            <PriorityBadge priority={fault.priority} />
          </div>
        </div>

        {/* Site Adresi - Ustanın Gideceği Ana Lokasyon */}
        {faultSite && (
          <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/30">
            <h4 className="font-semibold mb-2 flex items-center gap-2 text-blue-700 dark:text-blue-400">
              <Navigation className="w-5 h-5" />
              Site Adresi (Ustanın Gideceği Yer)
            </h4>
            
            <div className="space-y-2">
              {/* Site Adı */}
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-600" />
                <span className="font-semibold">{faultSite.name}</span>
              </div>
              
              {/* Adres + Navigasyon Butonu */}
              {faultSite.address && (
                <div className="p-3 rounded-lg bg-background/50 border border-border">
                  <p className="text-sm font-medium text-muted-foreground mb-1">📍 Adres:</p>
                  <p className="text-sm font-medium mb-2">{faultSite.address}</p>
                  
                  {/* Google Maps Navigasyon Butonu */}
                  <button
                    onClick={() => {
                      // Koordinat varsa koordinatla, yoksa adresle aç
                      const lat = faultSite.latitude || fault.latitude;
                      const lng = faultSite.longitude || fault.longitude;
                      let mapsUrl;
                      
                      if (lat && lng) {
                        // Koordinatla navigasyon
                        mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
                      } else {
                        // Adresle navigasyon
                        const fullAddress = `${faultSite.address}, ${faultSite.city || siteCity || ''}, Türkiye`;
                        mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;
                      }
                      
                      window.open(mapsUrl, '_blank');
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
                  >
                    <Navigation className="w-4 h-4" />
                    Google Maps ile Yol Tarifi Al
                  </button>
                </div>
              )}
              
              {/* İl ve İletişim */}
              <div className="flex flex-wrap gap-3 text-sm">
                {(faultSite.city || siteCity) && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500/20">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    <span className="font-medium">{faultSite.city || siteCity}</span>
                  </div>
                )}
                {faultSite.phone && (
                  <a 
                    href={`tel:${faultSite.phone}`}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 transition-colors cursor-pointer"
                  >
                    <Phone className="w-4 h-4 text-green-600" />
                    <span className="font-medium">{faultSite.phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* İç Lokasyon Bilgisi - Site İçindeki Konum */}
        {(fault.location_name || fault.location_description || fault.location_floor_section) && (
          <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30">
            <h4 className="font-semibold mb-2 flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
              <MapPin className="w-5 h-5" />
              İç Lokasyon (Site İçindeki Konum)
            </h4>
            
            <div className="space-y-2">
              {/* Lokasyon Adı ve Kat */}
              <div className="flex flex-wrap gap-3">
                {fault.location_name && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/20">
                    <Building2 className="w-4 h-4 text-emerald-600" />
                    <span className="font-medium">{fault.location_name}</span>
                  </div>
                )}
                {fault.location_floor_section && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500/20">
                    <Layers className="w-4 h-4 text-blue-600" />
                    <span className="font-medium">{fault.location_floor_section}</span>
                  </div>
                )}
              </div>
              
              {/* Yol Tarifi */}
              {fault.location_description && (
                <div className="p-3 rounded-lg bg-background/50 border border-border">
                  <p className="text-sm font-medium text-muted-foreground mb-1">📍 Yol Tarifi:</p>
                  <p className="text-sm font-medium">{fault.location_description}</p>
                </div>
              )}
              
              {/* Lokasyon Fotoğrafı kaldırıldı - sadece Lokasyonlar sayfasında gösterilecek */}
            </div>
          </div>
        )}

        {/* Filtreler */}
        <div className="flex flex-wrap gap-3 p-3 bg-accent/30 rounded-lg">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Usta ara (ad, telefon, firma)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-sm"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 rounded-lg border border-border bg-background text-sm"
          >
            <option value="">Tüm Tipler</option>
            <option value="hims_team">HIMS Ekibi</option>
            <option value="subcontractor">Taşeron</option>
            <option value="service_company">Servis Firması</option>
          </select>
          <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-background text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={filterAvailable}
              onChange={(e) => setFilterAvailable(e.target.checked)}
              className="w-4 h-4 rounded"
            />
            Sadece Müsait
          </label>
        </div>

        {/* Usta Listesi */}
        <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
          {techLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
              <p>Ustalar yükleniyor...</p>
            </div>
          ) : sortedTechnicians.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Kriterlere uygun usta bulunamadı</p>
              <p className="text-xs mt-1">({technicians.length} teknisyen yüklendi, filtreler: müsait={filterAvailable ? 'evet' : 'hayır'}, tip={filterType || 'hepsi'})</p>
            </div>
          ) : (
            sortedTechnicians.map((tech) => {
              const typeInfo = TECH_TYPES[tech.technician_type] || TECH_TYPES.subcontractor;
              const cost = calculateCost(tech);
              const inRegion = tech.service_regions?.some(r => 
                siteCity.toLowerCase().includes(r.toLowerCase()) || r.toLowerCase().includes(siteCity.toLowerCase())
              );

              return (
                <div
                  key={tech.id}
                  onClick={() => setSelectedTech(tech)}
                  className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedTech?.id === tech.id 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50 hover:bg-accent/30'
                  } ${!tech.is_available ? 'opacity-60' : ''}`}
                  data-testid={`tech-option-${tech.id}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className={`w-12 h-12 rounded-full bg-${typeInfo.color}-100 dark:bg-${typeInfo.color}-900/30 flex items-center justify-center flex-shrink-0`}>
                      <Wrench className={`w-6 h-6 text-${typeInfo.color}-600`} />
                    </div>

                    {/* Bilgiler */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold">{tech.full_name}</h4>
                        {tech.isContracted && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 font-medium">
                            📋 Sözleşmeli
                          </span>
                        )}
                        {tech.hasMatchingSpecialty && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                            ✓ Uzman
                          </span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full bg-${typeInfo.color}-100 text-${typeInfo.color}-700 dark:bg-${typeInfo.color}-900/30 dark:text-${typeInfo.color}-400`}>
                          {typeInfo.label}
                        </span>
                        {tech.is_available ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            🟢 Müsait
                          </span>
                        ) : tech.availability_status === 'off_duty' ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400">
                            ⚫ İzinli
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                            🔴 Meşgul
                          </span>
                        )}
                        {inRegion && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> Yakın Bölge
                          </span>
                        )}
                        {tech.distance !== null && (
                          <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${
                            tech.distance <= 50 
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                              : tech.distance <= 150 
                                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                          }`}>
                            <Navigation className="w-3 h-3" /> {tech.distance} km
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {tech.phone}
                        </span>
                        {tech.company_name && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {tech.company_name}
                          </span>
                        )}
                        {tech.average_rating > 0 && (
                          <span className="flex items-center gap-1 text-yellow-600">
                            ★ {tech.average_rating.toFixed(1)}
                          </span>
                        )}
                      </div>

                      {/* Bölgeler */}
                      {tech.service_regions?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {tech.service_regions.slice(0, 4).map((region, idx) => (
                            <span key={idx} className="text-xs px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">
                              {region}
                            </span>
                          ))}
                          {tech.service_regions.length > 4 && (
                            <span className="text-xs text-muted-foreground">+{tech.service_regions.length - 4}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Maliyet ve Mesafe */}
                    <div className="text-right flex-shrink-0">
                      {tech.distance !== null && (
                        <p className={`text-sm font-semibold mb-1 ${
                          tech.distance <= 50 ? 'text-green-600' : tech.distance <= 150 ? 'text-yellow-600' : 'text-orange-600'
                        }`}>
                          {tech.distance} km uzaklıkta
                        </p>
                      )}
                      <p className="text-lg font-bold text-primary">{cost.total} ₺</p>
                      <p className="text-xs text-muted-foreground">
                        {cost.laborCost} ₺ işçilik + {cost.travelCost} ₺ yol
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {tech.completed_jobs || 0} iş tamamlandı
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* İş Emri Ayarları */}
        {selectedTech && (
          <div className="p-4 rounded-xl border border-primary/30 bg-primary/5 space-y-4">
            <h4 className="font-semibold flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-primary" />
              Seçilen: {selectedTech.full_name}
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Garanti Durumu */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isWarranty}
                    onChange={(e) => setIsWarranty(e.target.checked)}
                    className="w-5 h-5 rounded border-2 border-primary text-primary"
                  />
                  <div>
                    <span className="font-medium">Garanti Kapsamında</span>
                    <p className="text-xs text-muted-foreground">
                      Garanti ücreti: {selectedTech.warranty_rate || 0} ₺
                    </p>
                  </div>
                </label>
              </div>

              {/* Planlanan Tarih */}
              <div>
                <label className="block text-sm font-medium mb-1">Planlanan Tarih</label>
                <input
                  type="datetime-local"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                />
              </div>

              {/* Tahmini Süre */}
              <div>
                <label className="block text-sm font-medium mb-1">Tahmini Süre (dk)</label>
                <input
                  type="number"
                  value={estimatedDuration}
                  onChange={(e) => setEstimatedDuration(parseInt(e.target.value) || 60)}
                  min="15"
                  step="15"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                />
              </div>

              {/* Garanti Notları */}
              {isWarranty && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Garanti Notları</label>
                  <textarea
                    value={warrantyNotes}
                    onChange={(e) => setWarrantyNotes(e.target.value)}
                    placeholder="Garanti kapsamı detayları..."
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm resize-none"
                  />
                </div>
              )}
            </div>

            {/* Maliyet Özeti */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <div>
                <p className="text-sm text-muted-foreground">Toplam Maliyet</p>
                <p className="text-2xl font-bold text-green-600">
                  {calculateCost(selectedTech).total} ₺
                </p>
              </div>
              <div className="text-right text-sm text-muted-foreground">
                <p>İşçilik: {isWarranty ? selectedTech.warranty_rate : selectedTech.per_job_rate} ₺</p>
                <p>Yol: {selectedTech.travel_allowance} ₺</p>
              </div>
            </div>
          </div>
        )}

        {/* Butonlar */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-border hover:bg-accent transition-colors"
          >
            İptal
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedTech || loading}
            className="px-6 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="confirm-assign-tech-btn"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Atanıyor...
              </>
            ) : (
              <>
                <Wrench className="w-4 h-4" />
                Usta Ata ve İş Emri Oluştur
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ==================== FAULT LIST VIEW ====================

const FaultListView = ({ faults, loading, onViewFault, onEditFault, onDeleteFault, onAssignTech, onRefresh, hideActionButtons = false }) => {
  const [expandedId, setExpandedId] = useState(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (faults.length === 0) {
    return (
      <Card className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">Arıza Kaydı Bulunamadı</h3>
        <p className="text-muted-foreground">Filtreleri değiştirmeyi deneyin veya yeni bir arıza ekleyin.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {faults.map((fault) => (
        <Card
          key={fault.id}
          className={`transition-all hover:shadow-md ${
            fault.priority === 'critical' ? 'border-l-4 border-l-red-500' :
            fault.priority === 'high' ? 'border-l-4 border-l-orange-500' : ''
          }`}
        >
          <div className="flex items-start gap-4">
            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-xs text-muted-foreground">{fault.id}</span>
                <PriorityBadge priority={fault.priority} />
                <StatusBadge status={fault.status} />
                {fault.source === 'ocpp' && (
                  <Badge variant="info">OCPP</Badge>
                )}
              </div>
              
              <h3 className="font-semibold text-lg mb-1">{fault.title}</h3>
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Zap className="w-4 h-4" />
                  {fault.station_name || fault.station_id}
                </span>
                {fault.site_name && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {fault.site_name}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(fault.created_at).toLocaleString('tr-TR')}
                </span>
                {fault.technician_name && (
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {fault.technician_name}
                  </span>
                )}
                {/* Konuma Git butonu - Koordinat varsa göster */}
                {(fault.latitude && fault.longitude) && (
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${fault.latitude},${fault.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md transition-colors text-xs font-medium"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`go-to-location-${fault.id}`}
                  >
                    <Navigation className="w-3 h-3" />
                    Konuma Git
                  </a>
                )}
              </div>

              {/* Expanded Details */}
              {expandedId === fault.id && (
                <div className="mt-4 pt-4 border-t border-border space-y-3">
                  {/* Konuma Git butonu - Koordinat varsa göster */}
                  {(fault.latitude && fault.longitude) && (
                    <div>
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${fault.latitude},${fault.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm font-medium"
                        data-testid="go-to-location-btn-list"
                      >
                        <Navigation className="w-4 h-4" />
                        Konuma Git
                      </a>
                    </div>
                  )}
                  {fault.description && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Açıklama</p>
                      <p className="text-sm">{fault.description}</p>
                    </div>
                  )}
                  {fault.error_code && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Hata Kodu</p>
                      <code className="text-sm bg-accent px-2 py-1 rounded">{fault.error_code}</code>
                    </div>
                  )}
                  {fault.resolution_notes && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Çözüm Notları</p>
                      <p className="text-sm">{fault.resolution_notes}</p>
                    </div>
                  )}
                  {fault.resolution_duration_hours && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Çözüm Süresi</p>
                      <p className="text-sm">{fault.resolution_duration_hours.toFixed(1)} saat</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Usta Ata Butonu - Sadece çözülmemiş arızalar için ve yardımcı değilse */}
              {!hideActionButtons && !['resolved', 'closed'].includes(fault.status) && (
                <button
                  onClick={() => onAssignTech(fault)}
                  className="p-2 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 text-orange-600 transition-colors"
                  title="Usta Ata"
                  data-testid={`fault-assign-tech-${fault.id}`}
                >
                  <Wrench className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={() => setExpandedId(expandedId === fault.id ? null : fault.id)}
                className="p-2 rounded-lg hover:bg-accent transition-colors"
                data-testid={`fault-expand-${fault.id}`}
              >
                {expandedId === fault.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              <button
                onClick={() => onViewFault(fault)}
                className="p-2 rounded-lg hover:bg-accent transition-colors"
                data-testid={`fault-view-${fault.id}`}
              >
                <Eye className="w-5 h-5" />
              </button>
              {/* Düzenle ve Sil butonları - yardımcı değilse göster */}
              {!hideActionButtons && (
                <>
                  <button
                    onClick={() => onEditFault(fault)}
                    className="p-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 transition-colors"
                    data-testid={`fault-edit-${fault.id}`}
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => onDeleteFault(fault)}
                    className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 transition-colors"
                    data-testid={`fault-delete-${fault.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

// ==================== TECHNICIANS VIEW ====================

const TechniciansView = ({ technicians, loading, onEdit, onDelete, onCreate, onRefresh }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Teknisyen Listesi ({technicians.length})</h3>
        <button
          onClick={onCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          data-testid="add-technician-btn"
        >
          <Plus className="w-4 h-4" />
          Teknisyen Ekle
        </button>
      </div>

      {technicians.length === 0 ? (
        <Card className="text-center py-12">
          <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Teknisyen Bulunamadı</h3>
          <p className="text-muted-foreground">Henüz teknisyen eklenmemiş.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {technicians.map((tech) => (
            <Card key={tech.id} className="hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                  {(tech.full_name || tech.name || 'XX').split(' ').map(n => n[0] || '').join('').slice(0, 2) || 'XX'}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold truncate">{tech.full_name || tech.name || 'İsimsiz'}</h4>
                  <p className="text-sm text-muted-foreground">{tech.specialty || (tech.specialties && tech.specialties[0]) || '-'}</p>
                  <div className="flex items-center gap-2 mt-2 text-sm">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{tech.phone || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant={(tech.open_faults_count || 0) > 0 ? 'warning' : 'success'}>
                      {tech.open_faults_count || 0} Açık Arıza
                    </Badge>
                    {!tech.is_active && <Badge variant="danger">Pasif</Badge>}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => onEdit(tech)}
                    className="p-1.5 rounded hover:bg-accent"
                    data-testid={`tech-edit-${tech.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(tech)}
                    className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600"
                    data-testid={`tech-delete-${tech.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== STATISTICS VIEW ====================

const StatisticsView = ({ stats, loading }) => {
  const { theme } = useTheme();
  
  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const statusData = stats.by_status?.map(s => ({
    name: STATUS_CONFIG[s.status]?.label || s.status,
    value: s.count
  })) || [];

  const priorityData = stats.by_priority?.map(p => ({
    name: PRIORITY_CONFIG[p.priority]?.label || p.priority,
    value: p.count
  })) || [];

  const trendData = stats.daily_trend?.map(d => ({
    date: d.date.slice(5),
    count: d.count
  })) || [];

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          title="Toplam Arıza"
          value={stats.total_faults}
          subtitle={`Son ${stats.period_days} gün`}
          icon={AlertTriangle}
          color="blue"
        />
        <StatCard
          title="Açık Arızalar"
          value={stats.open_faults}
          icon={Clock}
          color="orange"
        />
        <StatCard
          title="Kritik Arızalar"
          value={stats.critical_open}
          icon={AlertOctagon}
          color="red"
        />
        <StatCard
          title="Eskalasyon"
          value={stats.escalated_faults}
          subtitle="24+ saat açık"
          icon={TrendingUp}
          color="purple"
        />
        <StatCard
          title="Ort. Çözüm Süresi"
          value={`${stats.avg_resolution_hours || 0} saat`}
          icon={CheckCircle}
          color="green"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <Card>
          <h3 className="font-semibold mb-4">Durum Dağılımı</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={2}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Priority Distribution */}
        <Card>
          <h3 className="font-semibold mb-4">Öncelik Dağılımı</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={priorityData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? '#374151' : '#e5e7eb'} />
                <XAxis dataKey="name" tick={{ fill: theme === 'dark' ? '#9ca3af' : '#6b7280' }} />
                <YAxis tick={{ fill: theme === 'dark' ? '#9ca3af' : '#6b7280' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Daily Trend */}
      <Card>
        <h3 className="font-semibold mb-4">Günlük Arıza Trendi</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? '#374151' : '#e5e7eb'} />
              <XAxis dataKey="date" tick={{ fill: theme === 'dark' ? '#9ca3af' : '#6b7280' }} />
              <YAxis tick={{ fill: theme === 'dark' ? '#9ca3af' : '#6b7280' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
                  border: 'none',
                  borderRadius: '8px'
                }}
              />
              <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Top Stations */}
      {stats.top_stations?.length > 0 && (
        <Card>
          <h3 className="font-semibold mb-4">En Sorunlu İstasyonlar</h3>
          <div className="space-y-3">
            {stats.top_stations.map((station, index) => (
              <div key={station.station_id} className="flex items-center gap-4">
                <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                  {index + 1}
                </span>
                <div className="flex-1">
                  <p className="font-medium">{station.station_name || station.station_id}</p>
                </div>
                <Badge variant="warning">{station.count} arıza</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Error Codes */}
      {stats.error_codes?.length > 0 && (
        <Card>
          <h3 className="font-semibold mb-4">Hata Kodu Dağılımı</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {stats.error_codes.map((ec) => (
              <div key={ec.code} className="p-3 bg-accent rounded-lg text-center">
                <p className="font-mono text-sm font-medium">{ec.code}</p>
                <p className="text-2xl font-bold text-primary">{ec.count}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

// ==================== PHOTO UPLOAD SECTION ====================

const PhotoUploadSection = ({ faultId, existingPhotos = [], onPhotosChange }) => {
  const [photos, setPhotos] = useState(existingPhotos);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPhotos(existingPhotos);
  }, [existingPhotos]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Check max photos limit
    if (photos.length + files.length > 5) {
      setError('Maksimum 5 fotoğraf yüklenebilir');
      return;
    }

    setError('');
    setUploading(true);

    for (const file of files) {
      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        setError('Sadece JPG, PNG ve WEBP formatları desteklenir');
        continue;
      }

      // Validate file size (10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setError(`Dosya çok büyük (${(file.size / 1024 / 1024).toFixed(1)}MB). Maksimum 10MB yüklenebilir.`);
        continue;
      }

      try {
        // Convert to base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        // Upload to server
        const result = await faultsService.uploadPhoto(faultId, base64, file.name);
        setPhotos(prev => [...prev, result]);
        onPhotosChange && onPhotosChange();
      } catch (err) {
        setError(err.message || 'Fotoğraf yüklenemedi');
      }
    }

    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Fotoğrafı silmek istediğinizden emin misiniz?')) return;

    try {
      await faultsService.deletePhoto(faultId, photoId);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      onPhotosChange && onPhotosChange();
    } catch (err) {
      setError('Fotoğraf silinemedi');
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-border">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium flex items-center gap-2">
          <Camera className="w-4 h-4" />
          Fotoğraflar ({photos.length}/5)
        </h4>
        {photos.length < 5 && (
          <label className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg cursor-pointer hover:bg-primary/90 transition-colors">
            <Upload className="w-4 h-4" />
            {uploading ? 'Yükleniyor...' : 'Fotoğraf Ekle'}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={handleFileSelect}
              disabled={uploading}
              className="hidden"
              data-testid="photo-upload-input"
            />
          </label>
        )}
      </div>

      {error && (
        <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-lg">
          {error}
        </div>
      )}

      {photos.length === 0 ? (
        <div className="text-center py-6 bg-accent/30 rounded-lg">
          <Image className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">Henüz fotoğraf eklenmemiş</p>
          <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WEBP • Max 5MB</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {photos.map((photo) => (
            <div key={photo.id} className="relative group">
              <img
                src={faultsService.getPhotoUrl(faultId, photo.id)}
                alt={photo.description || 'Arıza fotoğrafı'}
                className="w-full h-24 object-cover rounded-lg border border-border"
              />
              <button
                onClick={() => handleDeletePhoto(photo.id)}
                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                title="Fotoğrafı sil"
              >
                <X className="w-3 h-3" />
              </button>
              {photo.description && (
                <p className="text-xs text-muted-foreground truncate mt-1">{photo.description}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== INSTALLATION FORM MODAL ====================

const InstallationFormModal = ({ isOpen, onClose, sites = [], onSubmit }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    site_id: '',
    location_id: '',
    device_brand: '',
    device_model: '',
    device_serial: '',
    device_power: '',
    installation_date: '',
    title: 'Yeni Cihaz Montajı',
    description: '',
    priority: 'medium',
    notes: ''
  });

  // Site seçilince lokasyonları yükle
  useEffect(() => {
    if (formData.site_id) {
      const fetchLocations = async () => {
        try {
          const token = localStorage.getItem('hims_token');
          const res = await fetch(`${API_URL}/api/locations?site_id=${formData.site_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setLocations(data);
          }
        } catch (error) {
          console.error('Lokasyonlar yüklenemedi:', error);
        }
      };
      fetchLocations();
    }
  }, [formData.site_id, API_URL]);

  const handleSubmit = async () => {
    if (!formData.site_id) {
      alert('Lütfen site seçin');
      return;
    }
    if (!formData.device_brand || !formData.device_model) {
      alert('Lütfen cihaz marka ve modelini girin');
      return;
    }

    setLoading(true);
    try {
      // Montaj işini arıza olarak kaydet (job_type: installation)
      const faultData = {
        site_id: formData.site_id,
        location_id: formData.location_id,
        station_id: '', // Montajda istasyon yok
        title: formData.title || `${formData.device_brand} ${formData.device_model} Montajı`,
        description: `Cihaz: ${formData.device_brand} ${formData.device_model}\nSeri No: ${formData.device_serial || '-'}\nGüç: ${formData.device_power || '-'}\nKurulum Tarihi: ${formData.installation_date || '-'}\n\n${formData.description}`,
        priority: formData.priority,
        job_type: 'installation',
        source: 'manual'
      };

      await onSubmit(faultData);
      onClose();
    } catch (error) {
      console.error('Montaj kaydı oluşturulamadı:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card rounded-2xl shadow-xl w-full max-w-2xl p-6 border border-border my-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center">
            <span className="text-2xl">🔩</span>
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Montaj/Kurulum Kaydı</h2>
            <p className="text-sm text-muted-foreground">Yeni cihaz montaj talebi oluşturun</p>
          </div>
        </div>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
          {/* Site Seçimi */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Site *</label>
            <select
              value={formData.site_id}
              onChange={(e) => setFormData(prev => ({ ...prev, site_id: e.target.value, location_id: '' }))}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
            >
              <option value="">Site seçin...</option>
              {sites.map(site => (
                <option key={site.id} value={site.id}>{site.name}</option>
              ))}
            </select>
          </div>

          {/* Lokasyon Seçimi */}
          {formData.site_id && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Lokasyon</label>
              <select
                value={formData.location_id}
                onChange={(e) => setFormData(prev => ({ ...prev, location_id: e.target.value }))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
              >
                <option value="">Lokasyon seçin (opsiyonel)...</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Cihaz Bilgileri */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Cihaz Markası *</label>
              <input
                type="text"
                value={formData.device_brand}
                onChange={(e) => setFormData(prev => ({ ...prev, device_brand: e.target.value }))}
                placeholder="ABB, Schneider, vb."
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Cihaz Modeli *</label>
              <input
                type="text"
                value={formData.device_model}
                onChange={(e) => setFormData(prev => ({ ...prev, device_model: e.target.value }))}
                placeholder="Terra AC W22-T"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Seri Numarası</label>
              <input
                type="text"
                value={formData.device_serial}
                onChange={(e) => setFormData(prev => ({ ...prev, device_serial: e.target.value }))}
                placeholder="SN12345678"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Güç (kW)</label>
              <input
                type="text"
                value={formData.device_power}
                onChange={(e) => setFormData(prev => ({ ...prev, device_power: e.target.value }))}
                placeholder="22 kW"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Kurulum Tarihi */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Planlanan Kurulum Tarihi</label>
            <input
              type="date"
              value={formData.installation_date}
              onChange={(e) => setFormData(prev => ({ ...prev, installation_date: e.target.value }))}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Öncelik */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Öncelik</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
            >
              <option value="low">Düşük</option>
              <option value="medium">Orta</option>
              <option value="high">Yüksek</option>
              <option value="critical">Kritik</option>
            </select>
          </div>

          {/* Açıklama */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Ek Notlar</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              placeholder="Montaj ile ilgili ek bilgiler..."
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary resize-none"
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 mt-6 pt-4 border-t border-border">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-muted-foreground hover:text-foreground transition-colors"
          >
            İptal
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
          >
            {loading ? 'Kaydediliyor...' : 'Montaj Kaydı Oluştur'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== MAINTENANCE FORM MODAL ====================

const MaintenanceFormModal = ({ isOpen, onClose, sites = [], stations = [], onSubmit }) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [loading, setLoading] = useState(false);
  const [filteredStations, setFilteredStations] = useState([]);
  const [formData, setFormData] = useState({
    site_id: '',
    station_id: '',
    maintenance_type: 'periodic',
    scheduled_date: '',
    title: 'Periyodik Bakım',
    description: '',
    priority: 'low',
    notes: ''
  });

  const MAINTENANCE_TYPES = [
    { value: 'periodic', label: 'Periyodik Bakım' },
    { value: 'visual', label: 'Görsel Kontrol' },
    { value: 'cleaning', label: 'Temizlik' },
    { value: 'calibration', label: 'Kalibrasyon' },
    { value: 'firmware', label: 'Firmware Güncelleme' },
    { value: 'other', label: 'Diğer' }
  ];

  // Site seçilince istasyonları filtrele
  useEffect(() => {
    if (formData.site_id) {
      const filtered = stations.filter(s => s.site_id === formData.site_id);
      setFilteredStations(filtered);
    } else {
      setFilteredStations([]);
    }
  }, [formData.site_id, stations]);

  const handleSubmit = async () => {
    if (!formData.site_id) {
      alert('Lütfen site seçin');
      return;
    }

    setLoading(true);
    try {
      const typeLabel = MAINTENANCE_TYPES.find(t => t.value === formData.maintenance_type)?.label || 'Bakım';
      
      const faultData = {
        site_id: formData.site_id,
        station_id: formData.station_id,
        title: formData.title || typeLabel,
        description: `Bakım Tipi: ${typeLabel}\nPlanlanan Tarih: ${formData.scheduled_date || '-'}\n\n${formData.description}`,
        priority: formData.priority,
        job_type: 'maintenance',
        maintenance_type: formData.maintenance_type,
        source: 'manual'
      };

      await onSubmit(faultData);
      onClose();
    } catch (error) {
      console.error('Bakım kaydı oluşturulamadı:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card rounded-2xl shadow-xl w-full max-w-lg p-6 border border-border my-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center">
            <span className="text-2xl">🔍</span>
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Periyodik Bakım Kaydı</h2>
            <p className="text-sm text-muted-foreground">Bakım planı oluşturun</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Site Seçimi */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Site *</label>
            <select
              value={formData.site_id}
              onChange={(e) => setFormData(prev => ({ ...prev, site_id: e.target.value, station_id: '' }))}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
            >
              <option value="">Site seçin...</option>
              {sites.map(site => (
                <option key={site.id} value={site.id}>{site.name}</option>
              ))}
            </select>
          </div>

          {/* İstasyon Seçimi */}
          {formData.site_id && filteredStations.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">İstasyon</label>
              <select
                value={formData.station_id}
                onChange={(e) => setFormData(prev => ({ ...prev, station_id: e.target.value }))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
              >
                <option value="">Tüm istasyonlar (opsiyonel)...</option>
                {filteredStations.map(station => (
                  <option key={station.id} value={station.id}>{station.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Bakım Tipi */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Bakım Tipi</label>
            <select
              value={formData.maintenance_type}
              onChange={(e) => setFormData(prev => ({ ...prev, maintenance_type: e.target.value }))}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
            >
              {MAINTENANCE_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Planlanan Tarih */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Planlanan Tarih</label>
            <input
              type="date"
              value={formData.scheduled_date}
              onChange={(e) => setFormData(prev => ({ ...prev, scheduled_date: e.target.value }))}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Öncelik */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Öncelik</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
            >
              <option value="low">Düşük</option>
              <option value="medium">Orta</option>
              <option value="high">Yüksek</option>
            </select>
          </div>

          {/* Açıklama */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Açıklama</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              placeholder="Bakım detayları..."
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary resize-none"
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 mt-6 pt-4 border-t border-border">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-muted-foreground hover:text-foreground transition-colors"
          >
            İptal
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
          >
            {loading ? 'Kaydediliyor...' : 'Bakım Kaydı Oluştur'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== FAULT FORM MODAL ====================

const FaultFormModal = ({ isOpen, onClose, fault, stations, technicians, onSubmit, sites = [], currentUser }) => {
  const [formData, setFormData] = useState({
    station_id: '',
    connector_id: 0,
    error_code: '',
    title: '',
    description: '',
    priority: 'medium',
    status: 'new',
    technician_id: '',
    resolution_notes: '',
    photo_notes: '',
    location_id: '',
    site_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [filteredStations, setFilteredStations] = useState([]);
  const [qrScanned, setQrScanned] = useState(false); // QR ile mi geldi?
  const qrScannerRef = useRef(null);
  
  // Manuel arama state'leri
  const [stationSearchQuery, setStationSearchQuery] = useState('');
  const [stationSearchResults, setStationSearchResults] = useState([]);
  const [selectedStationInfo, setSelectedStationInfo] = useState(null); // Seçilen istasyon bilgisi

  // Kullanıcının erişebileceği siteleri belirle
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isAdmin = currentUser?.role === 'admin';
  const userSiteId = currentUser?.site_id;
  
  // Kullanıcının görebileceği siteler
  const accessibleSites = (isSuperAdmin || isAdmin) ? sites : sites.filter(s => s.id === userSiteId);
  
  // Kullanıcının sitesi varsa otomatik seç
  const userSite = userSiteId ? sites.find(s => s.id === userSiteId) : null;

  useEffect(() => {
    if (fault) {
      setFormData({
        station_id: fault.station_id || '',
        connector_id: fault.connector_id || 0,
        error_code: fault.error_code || '',
        title: fault.title || '',
        description: fault.description || '',
        priority: fault.priority || 'medium',
        status: fault.status || 'new',
        technician_id: fault.technician_id || '',
        resolution_notes: fault.resolution_notes || '',
        photo_notes: fault.photo_notes || '',
        location_id: fault.location_id || '',
        site_id: fault.site_id || ''
      });
      // Mevcut fault için site bilgisini set et
      if (fault.site_id) {
        setSelectedSite({ id: fault.site_id, name: fault.site_name });
      }
      setSelectedLocation(fault.location_id ? {
        id: fault.location_id,
        name: fault.location_name,
        description: fault.location_description,
        photo_url: fault.location_photo_url
      } : null);
      setQrScanned(false);
    } else {
      // Yeni arıza - kullanıcının sitesini otomatik seç (admin değilse)
      const initialSiteId = (!isSuperAdmin && !isAdmin && userSiteId) ? userSiteId : '';
      const initialSite = initialSiteId ? sites.find(s => s.id === initialSiteId) : null;
      
      setFormData({
        station_id: '',
        connector_id: 0,
        error_code: '',
        title: '',
        description: '',
        priority: 'medium',
        status: 'new',
        technician_id: '',
        resolution_notes: '',
        photo_notes: '',
        location_id: '',
        site_id: initialSiteId
      });
      setSelectedSite(initialSite);
      setSelectedLocation(null);
      setSelectedAssistant(null);
      // filteredStations'ı burada temizleme - useEffect otomatik güncelleyecek
      setQrScanned(false);
    }
  }, [fault, isOpen, userSiteId, isSuperAdmin, isAdmin, sites]);

  // Site seçildiğinde istasyonları filtrele (sadece o sitenin istasyonları)
  useEffect(() => {
    if (!stations || !Array.isArray(stations) || stations.length === 0) {
      setFilteredStations([]);
      return;
    }
    
    if (formData.site_id) {
      // Belirli bir site seçildiyse sadece o sitenin istasyonları
      const siteStations = stations.filter(s => s.site_id === formData.site_id);
      setFilteredStations(siteStations);
    } else if (!isSuperAdmin && !isAdmin && userSiteId) {
      // Normal kullanıcı - kendi sitesinin istasyonları
      const siteStations = stations.filter(s => s.site_id === userSiteId);
      setFilteredStations(siteStations);
    } else if (isSuperAdmin || isAdmin) {
      // Admin/SuperAdmin - site seçmeden TÜM istasyonları göster
      setFilteredStations(stations);
    } else {
      setFilteredStations([]);
    }
  }, [formData.site_id, stations, isSuperAdmin, isAdmin, userSiteId]);

  // QR Scanner temizleme
  useEffect(() => {
    return () => {
      if (qrScannerRef.current) {
        qrScannerRef.current.clear().catch(() => {});
      }
    };
  }, []);

  // QR kod tarama başlat
  const startQRScanner = () => {
    setShowQRScanner(true);
    
    // Kısa bir gecikme ile scanner'ı başlat (DOM'un hazır olması için)
    setTimeout(() => {
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      };
      
      const scanner = new Html5QrcodeScanner("qr-reader", config, false);
      qrScannerRef.current = scanner;
      
      scanner.render(
        (decodedText) => {
          // QR kod başarıyla okundu
          handleQRCodeFound(decodedText);
          stopQRScanner();
        },
        (errorMessage) => {
          // Tarama devam ediyor, hata mesajını yoksay
        }
      );
    }, 100);
  };

  const stopQRScanner = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.clear().catch(() => {});
      qrScannerRef.current = null;
    }
    setShowQRScanner(false);
  };

  // QR kod bulunduğunda
  const handleQRCodeFound = async (qrCode) => {
    stopQRScanner();
    
    try {
      const token = localStorage.getItem('hims_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const res = await fetch(`${API_URL}/api/stations/find-by-qr?qr_code=${encodeURIComponent(qrCode)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        
        // QR ile tarandı flag'ini set et
        setQrScanned(true);
        
        // Hesap (Site) bilgisini göster
        if (data.site) {
          setSelectedSite(data.site);
        }
        
        // İstasyon bilgilerini doldur
        setFormData(prev => ({
          ...prev,
          station_id: data.station?.id || '',
          location_id: data.location?.id || '',
          site_id: data.site?.id || ''
        }));
        
        // Lokasyon bilgilerini göster
        if (data.location) {
          setSelectedLocation(data.location);
        }
        
        // Yardımcı bilgilerini göster ve otomatik ata
        if (data.assistant) {
          setSelectedAssistant(data.assistant);
          // Yardımcıyı teknisyen olarak ata (eğer teknisyen listesinde varsa)
          const matchingTech = technicians.find(t => t.id === data.assistant.id);
          if (matchingTech) {
            setFormData(prev => ({ ...prev, technician_id: data.assistant.id }));
          }
        }
        
        // Başarılı mesaj
        const siteName = data.site?.name || 'Bilinmeyen Hesap';
        const stationName = data.station?.name || 'Bilinmeyen İstasyon';
        const locationName = data.location?.name || '';
        alert(`✅ Bulunan Bilgiler:\n\n📍 Hesap: ${siteName}\n⚡ İstasyon: ${stationName}${locationName ? `\n🏢 Lokasyon: ${locationName}` : ''}`);
      } else {
        const error = await res.json();
        alert(error.detail || 'QR kod ile eşleşen istasyon bulunamadı. Kodu kontrol edin');
      }
    } catch (error) {
      console.error('QR kod arama hatası:', error);
      alert('Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin');
    }
  };

  // Hesap (Site) değiştiğinde istasyonları filtrele
  const handleSiteChange = (siteId) => {
    setFormData(prev => ({ ...prev, site_id: siteId, station_id: '' }));
    
    if (siteId) {
      const site = sites.find(s => s.id === siteId);
      setSelectedSite(site);
    } else {
      setSelectedSite(null);
    }
    
    // İstasyon seçimini temizle
    setSelectedLocation(null);
    setSelectedAssistant(null);
  };

  // İstasyon değiştiğinde lokasyon bilgisini getir
  const handleStationChange = async (stationId) => {
    setFormData(prev => ({ ...prev, station_id: stationId }));
    
    if (!stationId) {
      setSelectedLocation(null);
      setSelectedAssistant(null);
      return;
    }
    
    try {
      const token = localStorage.getItem('hims_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // İstasyon bilgisini al
      const stationRes = await fetch(`${API_URL}/api/stations/${stationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (stationRes.ok) {
        const station = await stationRes.json();
        
        // Eğer site seçilmemişse, istasyonun site'ını otomatik seç
        if (!formData.site_id && station.site_id) {
          const site = sites.find(s => s.id === station.site_id);
          if (site) {
            setSelectedSite(site);
            setFormData(prev => ({ ...prev, site_id: station.site_id }));
          }
        }
        
        if (station.location_id) {
          // Lokasyon bilgisini al
          const locRes = await fetch(`${API_URL}/api/locations/${station.location_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (locRes.ok) {
            const location = await locRes.json();
            setSelectedLocation(location);
            setFormData(prev => ({ ...prev, location_id: location.id }));
            
            // Yardımcı bilgisini al
            if (location.assistant_id) {
              setSelectedAssistant({
                id: location.assistant_id,
                full_name: location.assistant_name,
                phone: location.assistant_phone
              });
            } else {
              setSelectedAssistant(null);
            }
          }
        } else {
          setSelectedLocation(null);
          setSelectedAssistant(null);
        }
      }
    } catch (error) {
      console.error('İstasyon bilgisi alma hatası:', error);
    }
  };

  // Manuel istasyon arama fonksiyonu
  const searchStationsForFault = async (query) => {
    if (!query || query.length < 2) {
      setStationSearchResults([]);
      return;
    }
    
    try {
      const token = localStorage.getItem('hims_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      console.log('[FaultFormModal] Searching stations:', query);
      const res = await fetch(`${API_URL}/api/stations/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        console.log('[FaultFormModal] Search results:', data.results?.length || 0);
        setStationSearchResults(data.results || []);
      } else {
        console.error('[FaultFormModal] Search failed:', res.status);
      }
    } catch (error) {
      console.error('[FaultFormModal] Station search error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // İstasyon seçimi kontrolü - Montaj (installation) için istasyon zorunlu DEĞİL
    const stationId = formData.station_id || selectedStationInfo?.station?.id;
    
    // Sadece arıza ve bakım için istasyon zorunlu, montaj için değil
    if (!stationId && formData.job_type !== 'installation') {
      alert('Lütfen QR kod tarayarak veya arama kutusundan bir istasyon seçin');
      return;
    }
    
    // Eğer formData.station_id boşsa ama selectedStationInfo varsa, formData'yı güncelle
    const submitData = {
      ...formData,
      station_id: stationId || null, // Montajda null olabilir
      site_id: formData.site_id || selectedStationInfo?.site?.id || ''
    };
    
    setLoading(true);
    try {
      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Form submit error:', error);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-3 py-2 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all";
  const labelClass = "block text-sm font-medium mb-1.5";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={fault ? 'Arıza Düzenle' : 'Yeni Arıza Kaydı'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* QR Kod Tarayıcı Modal */}
        {showQRScanner && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
            <div className="bg-background rounded-lg p-4 max-w-lg w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-primary" />
                  QR Kod Tara
                </h3>
                <button
                  type="button"
                  onClick={stopQRScanner}
                  className="p-2 hover:bg-accent rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* html5-qrcode Scanner Container */}
              <div id="qr-reader" className="w-full rounded-lg overflow-hidden" />
              
              <p className="text-sm text-muted-foreground text-center mt-4">
                Cihazın üzerindeki QR kodu kameraya gösterin
              </p>
              
              {/* Manuel QR Kod Girişi */}
              <div className="mt-4 pt-4 border-t border-border space-y-2">
                <p className="text-sm text-muted-foreground text-center">
                  veya QR kodu manuel girin:
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="HIMS-xxxxx-xxxxx"
                    className={inputClass}
                    id="manual-qr-input"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('manual-qr-input');
                      if (input?.value) {
                        handleQRCodeFound(input.value);
                      }
                    }}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 whitespace-nowrap"
                  >
                    Ara
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* QR Kod Tara ve Manuel Arama */}
          {!fault && (
            <div className="md:col-span-2 space-y-3">
              {/* Montaj için istasyon opsiyonel uyarısı */}
              {formData.job_type === 'installation' && (
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-sm">
                  <span className="font-medium text-blue-700 dark:text-blue-400">ℹ️ Montaj/Kurulum talebi için istasyon seçimi opsiyoneldir.</span>
                  <p className="text-blue-600/80 dark:text-blue-400/80 mt-1">Yeni cihaz kurulumu olduğu için sistemde istasyon kaydı olmayabilir.</p>
                </div>
              )}
              
              {/* QR Kod Tara Butonu */}
              <button
                type="button"
                onClick={startQRScanner}
                className="w-full py-4 rounded-lg bg-gradient-to-r from-primary/10 to-primary/5 border-2 border-dashed border-primary/50 hover:border-primary hover:from-primary/20 hover:to-primary/10 transition-all flex items-center justify-center gap-3 group"
                data-testid="qr-scan-btn"
              >
                <Camera className="w-6 h-6 text-primary group-hover:scale-110 transition-transform" />
                <span className="font-semibold text-primary">QR Kod Tara {formData.job_type !== 'installation' ? '(Zorunlu)' : '(Opsiyonel)'}</span>
                <span className="text-sm text-muted-foreground">(Cihazın QR kodunu okutun)</span>
              </button>
              
              {/* Manuel Arama Kutusu - Sadece istasyon seçili değilken göster */}
              {!selectedStationInfo ? (
                <div className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 h-px bg-border"></div>
                    <span className="text-xs text-muted-foreground px-2">veya manuel ara</span>
                    <div className="flex-1 h-px bg-border"></div>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="İstasyon Adı, OCPP ID veya Seri No ile ara..."
                      value={stationSearchQuery || ''}
                      onChange={(e) => {
                        setStationSearchQuery(e.target.value);
                        if (e.target.value.length >= 2) {
                          searchStationsForFault(e.target.value);
                        } else {
                          setStationSearchResults([]);
                        }
                      }}
                      className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-background focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                      data-testid="station-search-input"
                    />
                  </div>
                  
                  {/* Arama Sonuçları */}
                  {stationSearchResults && stationSearchResults.length > 0 && (
                    <div className="mt-2 p-2 rounded-lg border border-border bg-card max-h-48 overflow-y-auto">
                      <p className="text-xs text-muted-foreground mb-2 px-2">
                        {stationSearchResults.length} sonuç bulundu
                      </p>
                      {stationSearchResults.map((result) => (
                        <button
                          key={result.station.id}
                          type="button"
                          onClick={() => {
                            // İstasyonu seç - önce formData'yı güncelle
                            const stationId = result.station.id;
                            const siteId = result.site?.id || '';
                            
                            setFormData(prev => ({ 
                              ...prev, 
                              station_id: stationId,
                              site_id: siteId || prev.site_id
                            }));
                            
                            // Site değişikliği
                            if (result.site) {
                              handleSiteChange(result.site.id);
                            }
                            
                            // Seçilen istasyon bilgisini kaydet
                            setSelectedStationInfo({
                              station: result.station,
                              site: result.site
                            });
                            
                            // station_id'yi formData'da direkt güncelle
                            setFormData(prev => ({ 
                              ...prev, 
                              station_id: stationId,
                              site_id: result.site?.id || prev.site_id
                            }));
                            
                            // Lokasyon bilgisini getir
                            handleStationChange(stationId);
                            
                            // Aramayı temizle
                            setStationSearchQuery('');
                            setStationSearchResults([]);
                          }}
                          className="w-full p-3 rounded-lg hover:bg-muted/50 transition-colors text-left flex items-center gap-3"
                        >
                          <div className="p-2 rounded-lg bg-primary/10">
                            <Zap className="h-4 w-4 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{result.station.name}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>OCPP: {result.station.ocpp_id}</span>
                              {result.station.serial_number && (
                                <>
                                  <span>•</span>
                                  <span>SN: {result.station.serial_number}</span>
                                </>
                              )}
                            </div>
                            {result.site && (
                              <p className="text-xs text-blue-500 mt-0.5">{result.site.name}</p>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                /* Seçilen İstasyon Kartı */
                <div className="mt-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-500/20">
                      <Zap className="w-5 h-5 text-green-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-green-600 font-medium uppercase tracking-wide">Seçilen İstasyon</p>
                      <p className="text-lg font-semibold truncate">{selectedStationInfo.station.name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>OCPP: {selectedStationInfo.station.ocpp_id}</span>
                        {selectedStationInfo.station.serial_number && (
                          <>
                            <span>•</span>
                            <span>SN: {selectedStationInfo.station.serial_number}</span>
                          </>
                        )}
                      </div>
                      {selectedStationInfo.site && (
                        <p className="text-xs text-blue-500 mt-0.5">Site: {selectedStationInfo.site.name}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedStationInfo(null);
                        setFormData(prev => ({ ...prev, station_id: '', site_id: '' }));
                        setStationSearchQuery('');
                        setStationSearchResults([]);
                      }}
                      className="px-3 py-1.5 text-sm rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                    >
                      Değiştir
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Hesap (Site/Fabrika/AVM) Bilgi Kartı - QR'dan veya seçimden gelir */}
          {selectedSite && (
            <div className="md:col-span-2 p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Building2 className="w-5 h-5 text-blue-500" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-blue-500 font-medium uppercase tracking-wide">Hesap / Site</p>
                  <p className="text-lg font-semibold">{selectedSite.name}</p>
                </div>
                {qrScanned && (
                  <span className="px-2 py-1 text-xs bg-green-500/20 text-green-600 rounded-full">
                    ✓ QR ile doğrulandı
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Hesap (Site) Seçimi - Sadece Admin/SuperAdmin için */}
          {!fault && !qrScanned && (isSuperAdmin || isAdmin) && (
            <div>
              <label className={labelClass}>Hesap (Site/Fabrika/AVM)</label>
              <select
                value={formData.site_id}
                onChange={(e) => handleSiteChange(e.target.value)}
                className={inputClass}
                data-testid="fault-site-select"
              >
                <option value="">Tüm hesaplar (istasyon seçince otomatik dolar)</option>
                {accessibleSites.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                İstasyon seçtiğinizde hesap otomatik belirlenir veya QR tarayın
              </p>
            </div>
          )}

          {/* Normal kullanıcı için site bilgisi (değiştirilemez) */}
          {!fault && !qrScanned && !isSuperAdmin && !isAdmin && userSite && (
            <div>
              <label className={labelClass}>Hesap (Site/Fabrika/AVM)</label>
              <div className={`${inputClass} bg-slate-100 dark:bg-slate-800 cursor-not-allowed`}>
                {userSite.name}
              </div>
              <p className="text-xs text-blue-500 mt-1">Siteniz otomatik seçildi. QR ile farklı cihaz da tarayabilirsiniz.</p>
            </div>
          )}

          {/* Station Select - Sadece QR ile veya arama ile seçilmişse göster (Kaldırıldı - arama kutusu kullanılıyor) */}
          {/* İstasyon seçimi artık sadece QR veya manuel arama ile yapılıyor */}
          {formData.station_id && (
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-green-500" />
                <span className="font-medium text-green-700 dark:text-green-400">
                  Seçilen İstasyon: {stations.find(s => s.id === formData.station_id)?.name || formData.station_id}
                </span>
                {!fault && !qrScanned && (
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, station_id: '', site_id: '', location_id: '' });
                      setSelectedSite(null);
                      setSelectedLocation(null);
                    }}
                    className="ml-auto text-xs text-red-500 hover:text-red-600"
                  >
                    Değiştir
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Connector */}
          <div>
            <label className={labelClass}>Konnektör No <span className="text-xs text-muted-foreground font-normal">(1-10 arası)</span></label>
            <input
              type="number"
              value={formData.connector_id}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 0;
                setFormData({ ...formData, connector_id: Math.min(val, 10) });
              }}
              className={inputClass}
              min="0"
              max="10"
              placeholder="örn: 1"
              data-testid="fault-connector-input"
            />
          </div>

          {/* Lokasyon Bilgisi (otomatik dolan) */}
          {selectedLocation && (
            <div className="md:col-span-2 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <div className="flex items-start gap-4">
                {/* Lokasyon Fotoğrafı kaldırıldı - sadece Lokasyonlar sayfasında gösterilecek */}
                
                <div className="flex-1">
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium uppercase tracking-wide mb-1">Lokasyon (İstasyonun Bulunduğu Yer)</p>
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-emerald-500" />
                    <span className="font-medium">{selectedLocation.name}</span>
                    {selectedLocation.floor_section && (
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <Layers className="w-3 h-3" />
                        {selectedLocation.floor_section}
                      </span>
                    )}
                  </div>
                  
                  {/* Yol Tarifi */}
                  {selectedLocation.description && (
                    <div className="text-sm text-muted-foreground mb-2 flex items-start gap-2">
                      <Navigation className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{selectedLocation.description}</span>
                    </div>
                  )}
                  
                  {/* Sorumlu Yardımcı */}
                  {selectedAssistant && (
                    <div className="flex items-center gap-2 text-sm bg-green-500/10 p-2 rounded-lg">
                      <User className="w-4 h-4 text-green-500" />
                      <span className="text-green-600 dark:text-green-400 font-medium">{selectedAssistant.full_name}</span>
                      {selectedAssistant.phone && (
                        <a href={`tel:${selectedAssistant.phone}`} className="text-primary hover:underline">
                          {selectedAssistant.phone}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Title */}
          <div className="md:col-span-2">
            <label className={labelClass}>Başlık *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className={inputClass}
              required
              placeholder="Arıza başlığı"
              data-testid="fault-title-input"
              onInvalid={(e) => e.target.setCustomValidity('Arıza başlığı girin')}
              onInput={(e) => e.target.setCustomValidity('')}
            />
          </div>

          {/* Error Code */}
          <div>
            <label className={labelClass}>Hata Kodu</label>
            <input
              type="text"
              value={formData.error_code}
              onChange={(e) => setFormData({ ...formData, error_code: e.target.value })}
              className={inputClass}
              placeholder="örn. ConnectorLockFailure"
              data-testid="fault-error-code-input"
            />
          </div>

          {/* Priority */}
          <div>
            <label className={labelClass}>Öncelik *</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className={inputClass}
              required
              data-testid="fault-priority-select"
              onInvalid={(e) => e.target.setCustomValidity('Öncelik seçin')}
              onInput={(e) => e.target.setCustomValidity('')}
            >
              {Object.entries(PRIORITY_CONFIG).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
          </div>

          {/* Status (only for edit) */}
          {fault && (
            <div>
              <label className={labelClass}>Durum</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className={inputClass}
                data-testid="fault-status-select"
              >
                {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Technician - Otomatik olarak lokasyonun yardımcısı gelir */}
          <div>
            <label className={labelClass}>
              Sorumlu Ata
              {selectedAssistant && (
                <span className="text-xs text-green-500 ml-2">(Lokasyon yardımcısı önerildi)</span>
              )}
            </label>
            <select
              value={formData.technician_id}
              onChange={(e) => setFormData({ ...formData, technician_id: e.target.value })}
              className={inputClass}
              data-testid="fault-technician-select"
            >
              <option value="">Atanmadı</option>
              {technicians.filter(t => t.is_active).map((t) => (
                <option key={t.id} value={t.id}>{t.full_name || t.name} ({t.open_faults_count || 0} açık arıza)</option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className={labelClass}>Açıklama</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className={`${inputClass} min-h-[100px]`}
              placeholder="Arıza hakkında detaylı bilgi..."
              data-testid="fault-description-input"
            />
          </div>

          {/* Resolution Notes (only for edit) */}
          {fault && (
            <div className="md:col-span-2">
              <label className={labelClass}>Çözüm Notları</label>
              <textarea
                value={formData.resolution_notes}
                onChange={(e) => setFormData({ ...formData, resolution_notes: e.target.value })}
                className={`${inputClass} min-h-[80px]`}
                placeholder="Yapılan işlemler, çözüm detayları..."
                data-testid="fault-resolution-input"
              />
            </div>
          )}
        </div>

        {/* Photo Upload Section (only for edit) */}
        {fault && (
          <PhotoUploadSection 
            faultId={fault.id} 
            existingPhotos={fault.photos || []}
            onPhotosChange={() => {}}
          />
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-border hover:bg-accent transition-colors"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="fault-submit-btn"
          >
            {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
            {fault ? 'Güncelle' : 'Oluştur'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ==================== TECHNICIAN FORM MODAL ====================

const TechnicianFormModal = ({ isOpen, onClose, technician, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    specialty: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (technician) {
      setFormData({
        name: technician.name || technician.full_name || '',
        email: technician.email || '',
        phone: technician.phone || '',
        specialty: technician.specialty || (technician.specialties && technician.specialties[0]) || '',
        is_active: technician.is_active !== false
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        specialty: '',
        is_active: true
      });
    }
  }, [technician, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Backend modeline uygun veri gönder
      const submitData = {
        full_name: formData.name,
        email: formData.email,
        phone: formData.phone
      };
      // specialties sadece doluysa gönder, boşsa hiç ekleme
      if (formData.specialty && formData.specialty.trim() !== '') {
        submitData.specialties = [formData.specialty];
      }
      console.log('Submitting data:', submitData);
      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Form submit error:', error);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-3 py-2 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all";
  const labelClass = "block text-sm font-medium mb-1.5";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={technician ? 'Teknisyen Düzenle' : 'Yeni Teknisyen'} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className={labelClass}>Ad Soyad *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={inputClass}
            required
            placeholder="Ahmet Yılmaz"
            data-testid="tech-name-input"
          />
        </div>

        <div>
          <label className={labelClass}>Email *</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className={inputClass}
            required
            placeholder="ahmet@sirket.com"
            data-testid="tech-email-input"
          />
        </div>

        <div>
          <label className={labelClass}>Telefon *</label>
          <input
            type="text"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className={inputClass}
            required
            placeholder="+90 532 111 2233"
            data-testid="tech-phone-input"
          />
        </div>

        <div>
          <label className={labelClass}>Uzmanlık Alanı</label>
          <input
            type="text"
            value={formData.specialty}
            onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
            className={inputClass}
            placeholder="Elektrik & Elektronik"
            data-testid="tech-specialty-input"
          />
        </div>

        {technician && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="tech-active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 rounded border-border"
              data-testid="tech-active-checkbox"
            />
            <label htmlFor="tech-active" className="text-sm">Aktif</label>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-border hover:bg-accent transition-colors"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="tech-submit-btn"
          >
            {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
            {technician ? 'Güncelle' : 'Oluştur'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

// ==================== FAULT DETAIL MODAL ====================

const FaultDetailModal = ({ isOpen, onClose, fault }) => {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (isOpen && fault) {
      loadHistory();
    }
  }, [isOpen, fault]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const data = await faultsService.getHistory(fault.id);
      setHistory(data);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  if (!fault) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Arıza Detayı: ${fault.id}`} size="lg">
      <div className="space-y-6">
        {/* Header Info */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-semibold">{fault.title}</h3>
            <div className="flex items-center gap-3 mt-2">
              <PriorityBadge priority={fault.priority} />
              <StatusBadge status={fault.status} />
              {fault.source === 'ocpp' && <Badge variant="info">OCPP Otomatik</Badge>}
            </div>
          </div>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">İstasyon</p>
            <p className="font-medium">{fault.station_name || fault.station_id}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Site</p>
            <p className="font-medium">{fault.site_name || '-'}</p>
          </div>
          
          {/* Konuma Git butonu - Koordinat varsa göster */}
          {(fault.latitude && fault.longitude) && (
            <div className="col-span-2">
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${fault.latitude},${fault.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm font-medium"
                data-testid="go-to-location-btn"
              >
                <Navigation className="w-4 h-4" />
                Konuma Git (Google Maps)
              </a>
            </div>
          )}
          
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Konnektör</p>
            <p className="font-medium">{fault.connector_id || '-'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Hata Kodu</p>
            <code className="bg-accent px-2 py-1 rounded text-sm">{fault.error_code || '-'}</code>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Oluşturulma</p>
            <p className="font-medium">{new Date(fault.created_at).toLocaleString('tr-TR')}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Teknisyen</p>
            <p className="font-medium">{fault.technician_name || 'Atanmadı'}</p>
          </div>
          {fault.resolved_at && (
            <>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Çözüldü</p>
                <p className="font-medium">{new Date(fault.resolved_at).toLocaleString('tr-TR')}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Çözüm Süresi</p>
                <p className="font-medium">{fault.resolution_duration_hours?.toFixed(1)} saat</p>
              </div>
            </>
          )}
        </div>

        {/* Usta Konum Takibi - Sadece atanmış usta varsa */}
        {(fault.technician_id || fault.assigned_technician_id || fault.technician_name) && (
          <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30">
            <TechnicianLocationMap faultId={fault.id} />
          </div>
        )}

        {/* Description */}
        {fault.description && (
          <div>
            <h4 className="font-medium mb-2">Açıklama</h4>
            <p className="text-muted-foreground">{fault.description}</p>
          </div>
        )}

        {/* İç Lokasyon Bilgisi - Teknisyen için kritik */}
        {(fault.location_name || fault.location_description) && (
          <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30">
            <h4 className="font-semibold mb-3 flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
              <MapPin className="w-5 h-5" />
              İç Lokasyon Bilgisi
            </h4>
            
            <div className="space-y-3">
              {/* Lokasyon Adı ve Kat */}
              <div className="flex flex-wrap gap-4">
                {fault.location_name && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{fault.location_name}</span>
                  </div>
                )}
                {fault.location_floor_section && (
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-muted-foreground" />
                    <span>{fault.location_floor_section}</span>
                  </div>
                )}
              </div>
              
              {/* Yol Tarifi */}
              {fault.location_description && (
                <div className="p-3 rounded-lg bg-background/50 border border-border">
                  <p className="text-sm font-medium text-muted-foreground mb-1">Yol Tarifi:</p>
                  <p className="text-sm">{fault.location_description}</p>
                </div>
              )}
              
              {/* Lokasyon Fotoğrafı kaldırıldı - sadece Lokasyonlar sayfasında gösterilecek */}
            </div>
          </div>
        )}

        {/* Resolution Notes */}
        {fault.resolution_notes && (
          <div>
            <h4 className="font-medium mb-2">Çözüm Notları</h4>
            <p className="text-muted-foreground">{fault.resolution_notes}</p>
          </div>
        )}

        {/* Photos */}
        {fault.photos && fault.photos.length > 0 && (
          <div>
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Camera className="w-4 h-4" />
              Fotoğraflar ({fault.photos.length})
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {fault.photos.map((photo) => (
                <a
                  key={photo.id}
                  href={faultsService.getPhotoUrl(fault.id, photo.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group"
                >
                  <img
                    src={faultsService.getPhotoUrl(fault.id, photo.id)}
                    alt={photo.description || 'Arıza fotoğrafı'}
                    className="w-full h-32 object-cover rounded-lg border border-border group-hover:border-primary transition-colors"
                  />
                  {photo.description && (
                    <p className="text-xs text-muted-foreground mt-1 truncate">{photo.description}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {new Date(photo.uploaded_at).toLocaleString('tr-TR')}
                  </p>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* History */}
        <div>
          <h4 className="font-medium mb-3 flex items-center gap-2">
            <History className="w-4 h-4" />
            Değişiklik Geçmişi
          </h4>
          {loadingHistory ? (
            <div className="flex justify-center py-4">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : history.length === 0 ? (
            <p className="text-muted-foreground text-sm">Henüz geçmiş kaydı yok.</p>
          ) : (
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {history.map((h) => (
                <div key={h.id} className="flex gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <div>
                    <p className="font-medium">
                      {h.action === 'created' ? 'Oluşturuldu' :
                       h.action === 'status_changed' ? 'Durum değişti' :
                       h.action === 'priority_changed' ? 'Öncelik değişti' :
                       h.action === 'technician_changed' ? 'Teknisyen atandı' :
                       h.action}
                    </p>
                    {h.old_value && h.new_value && (
                      <p className="text-muted-foreground">
                        {h.old_value} → {h.new_value}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {h.changed_by_name || h.changed_by} • {new Date(h.timestamp).toLocaleString('tr-TR')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

// ==================== MAIN PAGE COMPONENT ====================

const FaultManagementPage = () => {
  // Get current user from auth context
  const { user: currentUser, isYardimci, isHesapYoneticisi, isSimulationActive, getEffectiveRole, getEffectiveSiteId, isSuperAdmin, isAdmin } = useAuth();
  
  // Simülasyon için rol kontrolü
  const effectiveRole = getEffectiveRole ? getEffectiveRole() : currentUser?.role;
  const effectiveSiteId = getEffectiveSiteId ? getEffectiveSiteId() : currentUser?.site_id;
  const simulationActive = isSimulationActive ? isSimulationActive() : false;
  
  const isEffectiveYardimci = effectiveRole === ROLES.YARDIMCI || effectiveRole === 'yardimci' || isYardimci();
  const isEffectiveHesapYoneticisi = effectiveRole === ROLES.HESAP_YONETICISI || effectiveRole === 'hesap_yoneticisi' || effectiveRole === 'site_manager';
  
  // Simülasyon aktifken effectiveRole'e göre kontrol et
  const isEffectiveSuperAdmin = simulationActive 
    ? effectiveRole === 'super_admin' 
    : (isSuperAdmin && isSuperAdmin());
  const isEffectiveAdmin = simulationActive 
    ? effectiveRole === 'admin' 
    : (isAdmin && isAdmin());
  
  // Yardımcı için düzenle, sil, usta atama butonlarını gizle
  const hideActionButtons = isEffectiveYardimci;
  
  // Mock Veri butonu sadece super_admin ve admin görsün (simülasyonda da geçerli)
  const showMockDataButton = isEffectiveSuperAdmin || isEffectiveAdmin;
  
  // State
  const [activeTab, setActiveTab] = useState('list');
  const [faults, setFaults] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [stations, setStations] = useState([]);
  const [sites, setSites] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [criticalFaults, setCriticalFaults] = useState([]);

  // Modal State
  const [faultModalOpen, setFaultModalOpen] = useState(false);
  const [selectedFault, setSelectedFault] = useState(null);
  const [faultDetailOpen, setFaultDetailOpen] = useState(false);
  const [techModalOpen, setTechModalOpen] = useState(false);
  const [selectedTech, setSelectedTech] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [assignTechModalOpen, setAssignTechModalOpen] = useState(false);
  const [faultToAssign, setFaultToAssign] = useState(null);
  
  // Job Type Modal State
  const [jobTypeModalOpen, setJobTypeModalOpen] = useState(false);
  const [installationModalOpen, setInstallationModalOpen] = useState(false);
  const [maintenanceModalOpen, setMaintenanceModalOpen] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    include_closed: false
  });

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0
  });

  // Load Data
  const loadFaults = useCallback(async () => {
    setLoading(true);
    try {
      const result = await faultsService.list({
        ...filters,
        page: pagination.page,
        page_size: pagination.page_size
      });
      
      let filteredFaults = result.faults || [];
      
      // Hesap Yöneticisi için sadece kendi sitesinin arızalarını göster
      if (isEffectiveHesapYoneticisi && effectiveSiteId) {
        filteredFaults = filteredFaults.filter(f => f.site_id === effectiveSiteId);
      }
      
      // Yardımcı için sadece atandığı sitenin arızalarını göster
      if (isEffectiveYardimci && effectiveSiteId) {
        filteredFaults = filteredFaults.filter(f => f.site_id === effectiveSiteId);
      }
      
      setFaults(filteredFaults);
      setPagination(prev => ({
        ...prev,
        total: filteredFaults.length,
        total_pages: Math.ceil(filteredFaults.length / pagination.page_size)
      }));
    } catch (error) {
      console.error('Failed to load faults:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.page_size, isEffectiveHesapYoneticisi, isEffectiveYardimci, effectiveSiteId]);

  const loadTechnicians = useCallback(async () => {
    try {
      // Yeni API kullan - cache bypass için timestamp ekle
      const token = localStorage.getItem('hims_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/technicians?_t=${Date.now()}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });
      if (res.ok) {
        const data = await res.json();
        console.log('Technicians loaded:', data.length);
        setTechnicians([...data]); // Yeni array referansı ile state güncelle
      }
    } catch (error) {
      console.error('Failed to load technicians:', error);
    }
  }, []);

  const loadStations = useCallback(async () => {
    try {
      const data = await stationsService.list();
      setStations(data);
    } catch (error) {
      console.error('Failed to load stations:', error);
    }
  }, []);

  const loadSites = useCallback(async () => {
    try {
      const data = await sitesService.list();
      setSites(data);
    } catch (error) {
      console.error('Failed to load sites:', error);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const data = await faultsService.getStats(30);
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  const loadCriticalFaults = useCallback(async () => {
    try {
      const data = await faultsService.getCritical(5);
      setCriticalFaults(data);
    } catch (error) {
      console.error('Failed to load critical faults:', error);
    }
  }, []);

  useEffect(() => {
    loadFaults();
    loadTechnicians();
    loadStations();
    loadSites();
    loadStats();
    loadCriticalFaults();
  }, []);

  useEffect(() => {
    loadFaults();
  }, [filters, pagination.page]);

  // Handlers
  const handleCreateFault = async (data) => {
    await faultsService.create(data);
    loadFaults();
    loadStats();
    loadCriticalFaults();
  };

  const handleUpdateFault = async (data) => {
    await faultsService.update(selectedFault.id, data);
    loadFaults();
    loadStats();
    loadCriticalFaults();
  };

  const handleDeleteFault = async () => {
    if (!itemToDelete) return;
    await faultsService.delete(itemToDelete.id);
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
    loadFaults();
    loadStats();
  };

  const handleCreateTech = async (data) => {
    await techniciansService.create(data);
    loadTechnicians();
  };

  const handleUpdateTech = async (data) => {
    try {
      console.log('Updating technician:', selectedTech.id, data);
      await techniciansService.update(selectedTech.id, data);
      console.log('Update successful, refreshing list...');
      // Önce listeyi yenile
      await loadTechnicians();
      // Sonra modal'ı kapat
      setTechModalOpen(false);
      setSelectedTech(null);
    } catch (error) {
      console.error('Update failed:', error);
    }
  };

  const handleDeleteTech = async () => {
    if (!itemToDelete) return;
    await techniciansService.delete(itemToDelete.id);
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
    loadTechnicians();
  };

  const handleSeedData = async () => {
    if (!window.confirm('Mock arıza verileri oluşturulacak. Devam etmek istiyor musunuz?')) return;
    try {
      await faultsService.seed();
      loadFaults();
      loadTechnicians();
      loadStats();
      loadCriticalFaults();
      alert('Mock veriler oluşturuldu!');
    } catch (error) {
      console.error('Seed failed:', error);
      alert('Veri oluşturma hatası: ' + error.message);
    }
  };

  const openDeleteConfirm = (item, type) => {
    setItemToDelete({ ...item, type });
    setDeleteConfirmOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="fault-management-page">
      {/* Critical Faults Banner */}
      {criticalFaults.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertOctagon className="w-6 h-6 text-red-500 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-500">Kritik Arızalar ({criticalFaults.length})</h3>
              <p className="text-sm text-muted-foreground">
                {criticalFaults.map(f => f.title).join(' | ')}
              </p>
            </div>
            <button
              onClick={() => {
                setFilters({ ...filters, priority: 'critical' });
                setActiveTab('list');
              }}
              className="px-3 py-1.5 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              Görüntüle
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Arıza Yönetimi</h1>
          <p className="text-muted-foreground">İstasyon arızalarını takip edin ve yönetin</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Mock Veri butonu sadece super_admin ve admin görsün */}
          {showMockDataButton && (
            <button
              onClick={handleSeedData}
              className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-accent transition-colors flex items-center gap-2"
              data-testid="seed-data-btn"
            >
              <Database className="w-4 h-4" />
              Mock Veri
            </button>
          )}
          <button
            onClick={() => {
              loadFaults();
              loadStats();
              loadCriticalFaults();
            }}
            className="p-2 rounded-lg hover:bg-accent transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          {/* Yeni İş Talebi butonu - Yardımcı için gizli */}
          {!hideActionButtons && (
            <button
              onClick={() => setJobTypeModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              data-testid="add-fault-btn"
            >
              <Plus className="w-4 h-4" />
              Yeni İş Talebi
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2 p-1 bg-accent/50 rounded-xl">
        <TabButton
          active={activeTab === 'list'}
          onClick={() => setActiveTab('list')}
          icon={List}
          label="Arıza Listesi"
          badge={pagination.total}
        />
        <TabButton
          active={activeTab === 'stats'}
          onClick={() => setActiveTab('stats')}
          icon={BarChart3}
          label="İstatistikler"
        />
      </div>

      {/* Filters (only for list tab) */}
      {activeTab === 'list' && (
        <Card className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filtreler:</span>
          </div>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm"
            data-testid="filter-status"
          >
            <option value="">Tüm Durumlar</option>
            {Object.entries(STATUS_CONFIG).map(([key, val]) => (
              <option key={key} value={key}>{val.label}</option>
            ))}
          </select>

          <select
            value={filters.priority}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
            className="px-3 py-1.5 rounded-lg border border-border bg-background text-sm"
            data-testid="filter-priority"
          >
            <option value="">Tüm Öncelikler</option>
            {Object.entries(PRIORITY_CONFIG).map(([key, val]) => (
              <option key={key} value={key}>{val.label}</option>
            ))}
          </select>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={filters.include_closed}
              onChange={(e) => setFilters({ ...filters, include_closed: e.target.checked })}
              className="w-4 h-4 rounded border-border"
              data-testid="filter-include-closed"
            />
            Kapatılmış arızaları göster
          </label>

          {(filters.status || filters.priority) && (
            <button
              onClick={() => setFilters({ status: '', priority: '', include_closed: false })}
              className="text-sm text-primary hover:underline"
            >
              Filtreleri Temizle
            </button>
          )}
        </Card>
      )}

      {/* Content */}
      {activeTab === 'list' && (
        <>
          <FaultListView
            faults={faults}
            loading={loading}
            onViewFault={(f) => {
              setSelectedFault(f);
              setFaultDetailOpen(true);
            }}
            onEditFault={(f) => {
              setSelectedFault(f);
              setFaultModalOpen(true);
            }}
            onDeleteFault={(f) => openDeleteConfirm(f, 'fault')}
            onAssignTech={(f) => {
              setFaultToAssign(f);
              loadTechnicians(); // Teknisyen listesini tazele
              setAssignTechModalOpen(true);
            }}
            onRefresh={loadFaults}
            hideActionButtons={hideActionButtons}
          />

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex justify-center items-center gap-2">
              <button
                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-1.5 rounded-lg border border-border hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Önceki
              </button>
              <span className="text-sm text-muted-foreground">
                Sayfa {pagination.page} / {pagination.total_pages}
              </span>
              <button
                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                disabled={pagination.page === pagination.total_pages}
                className="px-3 py-1.5 rounded-lg border border-border hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sonraki
              </button>
            </div>
          )}
        </>
      )}

      {activeTab === 'stats' && (
        <StatisticsView stats={stats} loading={!stats} />
      )}

      {/* Modals */}
      <FaultFormModal
        isOpen={faultModalOpen}
        onClose={() => {
          setFaultModalOpen(false);
          setSelectedFault(null);
        }}
        fault={selectedFault}
        stations={stations}
        sites={sites}
        technicians={technicians}
        currentUser={currentUser}
        onSubmit={selectedFault ? handleUpdateFault : handleCreateFault}
      />

      <FaultDetailModal
        isOpen={faultDetailOpen}
        onClose={() => {
          setFaultDetailOpen(false);
          setSelectedFault(null);
        }}
        fault={selectedFault}
      />

      <TechnicianFormModal
        isOpen={techModalOpen}
        onClose={() => {
          setTechModalOpen(false);
          setSelectedTech(null);
        }}
        technician={selectedTech}
        onSubmit={selectedTech ? handleUpdateTech : handleCreateTech}
      />

      {/* Usta Atama Modal */}
      <AssignTechnicianModal
        isOpen={assignTechModalOpen}
        onClose={() => {
          setAssignTechModalOpen(false);
          setFaultToAssign(null);
        }}
        fault={faultToAssign}
        technicians={technicians}
        sites={sites}
        onAssign={(workOrder) => {
          loadFaults();
          setAssignTechModalOpen(false);
          setFaultToAssign(null);
        }}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setItemToDelete(null);
        }}
        title="Silme Onayı"
        size="sm"
      >
        <div className="space-y-4">
          <p>
            <strong>{itemToDelete?.title || itemToDelete?.name || itemToDelete?.id}</strong> 
            {' '}kaydını silmek istediğinizden emin misiniz?
          </p>
          <p className="text-sm text-muted-foreground">Bu işlem geri alınamaz.</p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setDeleteConfirmOpen(false);
                setItemToDelete(null);
              }}
              className="px-4 py-2 rounded-lg border border-border hover:bg-accent"
            >
              İptal
            </button>
            <button
              onClick={itemToDelete?.type === 'fault' ? handleDeleteFault : handleDeleteTech}
              className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              Sil
            </button>
          </div>
        </div>
      </Modal>

      {/* Job Type Selection Modal */}
      <JobTypeSelectModal
        isOpen={jobTypeModalOpen}
        onClose={() => setJobTypeModalOpen(false)}
        onSelect={(type) => {
          setJobTypeModalOpen(false);
          if (type === 'fault') {
            setSelectedFault(null);
            setFaultModalOpen(true);
          } else if (type === 'installation') {
            setInstallationModalOpen(true);
          } else if (type === 'maintenance') {
            setMaintenanceModalOpen(true);
          }
        }}
      />

      {/* Installation Form Modal */}
      <InstallationFormModal
        isOpen={installationModalOpen}
        onClose={() => setInstallationModalOpen(false)}
        sites={sites}
        onSubmit={handleCreateFault}
      />

      {/* Maintenance Form Modal */}
      <MaintenanceFormModal
        isOpen={maintenanceModalOpen}
        onClose={() => setMaintenanceModalOpen(false)}
        sites={sites}
        stations={stations}
        onSubmit={handleCreateFault}
      />
    </div>
  );
};

export default FaultManagementPage;
